import { useEffect, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { TableSkeleton } from "@/components/loading-skeletons";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Inbox, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { slugify } from "@/lib/slug";
import { ImageUpload } from "@/components/image-upload";
import { useStorageUrl } from "@/components/storage-image";

function CategoryThumb({ value, name }: { value: string | null; name: string }) {
  const url = useStorageUrl("store", value);
  if (!value) {
    return (
      <div className="w-12 h-12 rounded-lg border bg-muted flex items-center justify-center text-muted-foreground">
        <ImageIcon className="w-5 h-5" />
      </div>
    );
  }
  return (
    <div className="w-12 h-12 rounded-lg border bg-muted overflow-hidden flex items-center justify-center">
      {url ? (
        <img src={url} alt={name} loading="lazy" className="w-full h-full object-cover" />
      ) : (
        <ImageIcon className="w-5 h-5 text-muted-foreground" />
      )}
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/store/categories")({
  head: () => ({ meta: [{ title: "Kategori Store — MI Al-Munajah Admin" }] }),
  component: CategoriesPage,
});

type Cat = {
  id: string;
  name: string;
  slug: string | null;
  icon: string | null;
  icon_color: string | null;
  image_url: string | null;
  sort_order: number;
  is_active: boolean;
};

const empty = { name: "", slug: "", icon: "Package", icon_color: "#3B82F6", image_url: null as string | null, sort_order: 0, is_active: true };

function CategoriesPage() {
  const [rows, setRows] = useCachedState<Cat[]>("store.categories:rows", []);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Cat | null>(null);
  const [form, setForm] = useState(empty);
  const [delId, setDelId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("store_categories").select("*").order("sort_order").order("name");
    if (error) toast.error("Gagal memuat: " + error.message);
    else setRows((data ?? []) as Cat[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r: Cat) => {
    setEditing(r);
    setForm({
      name: r.name, slug: r.slug ?? "", icon: r.icon ?? "Package",
      icon_color: r.icon_color ?? "#3B82F6", image_url: r.image_url ?? null,
      sort_order: r.sort_order, is_active: r.is_active,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      slug: (form.slug || slugify(form.name)).trim(),
      icon: form.icon.trim() || null,
      icon_color: form.icon_color || null,
      image_url: form.image_url || null,
      sort_order: Number(form.sort_order) || 0,
      is_active: form.is_active,
    };
    const { error } = editing
      ? await supabase.from("store_categories").update(payload).eq("id", editing.id)
      : await supabase.from("store_categories").insert(payload);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Kategori diperbarui" : "Kategori dibuat");
    setOpen(false);
    load();
  };

  const remove = async () => {
    if (!delId) return;
    setBusy(true);
    const { error } = await supabase.from("store_categories").delete().eq("id", delId);
    setBusy(false);
    setDelId(null);
    if (error) return toast.error("Gagal menghapus: " + error.message);
    toast.success("Kategori dihapus");
    load();
  };

  const filtered = rows.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader
        title="Kategori Produk"
        description="Kelola kategori yang tampil pada katalog Toko Sekolah."
        actions={<Button onClick={openCreate}><Plus className="w-4 h-4" /> Tambah Kategori</Button>}
      />
      <Card className="p-4 rounded-2xl">
        <div className="mb-3">
          <Input placeholder="Cari kategori…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />
        </div>
        <div className="rounded-xl border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Gambar</TableHead>
                <TableHead>Nama</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Icon</TableHead>
                <TableHead className="text-right">Urutan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableSkeleton columns={7} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-12">
                    <div className="flex flex-col items-center text-muted-foreground gap-2">
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center"><Inbox className="w-6 h-6" /></div>
                      <div className="font-medium text-foreground">Belum ada kategori</div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <CategoryThumb value={r.image_url} name={r.name} />
                  </TableCell>
                  <TableCell className="font-medium">
                    {r.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{r.slug || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{r.icon || "—"}</TableCell>
                  <TableCell className="text-right">{r.sort_order}</TableCell>
                  <TableCell>
                    {r.is_active
                      ? <Badge variant="outline" className="bg-success/15 text-success border-success/30">Aktif</Badge>
                      : <Badge variant="outline" className="bg-muted text-muted-foreground">Nonaktif</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDelId(r.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Kategori" : "Tambah Kategori"}</DialogTitle>
            <DialogDescription>Kategori akan tampil pada filter katalog aplikasi Android.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama <span className="text-destructive">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Contoh: Seragam" />
            </div>
            <div>
              <Label>Slug</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="Otomatis dari nama jika kosong" />
            </div>
            <div>
              <Label>Gambar Kategori</Label>
              <ImageUpload bucket="store" value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} />
              <p className="text-xs text-muted-foreground mt-1">Gambar akan tampil pada katalog Toko Sekolah.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nama Icon (Lucide)</Label>
                <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="Package" />
              </div>
              <div>
                <Label>Warna Icon</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={form.icon_color} onChange={(e) => setForm({ ...form, icon_color: e.target.value })} className="h-9 w-12 rounded border" />
                  <Input value={form.icon_color} onChange={(e) => setForm({ ...form, icon_color: e.target.value })} />
                </div>
              </div>
            </div>
            <div>
              <Label>Urutan</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium">Aktif</div>
                <div className="text-xs text-muted-foreground">Hanya kategori aktif yang tampil di Android.</div>
              </div>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={busy}>{editing ? "Simpan" : "Tambah"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus kategori?</AlertDialogTitle>
            <AlertDialogDescription>Produk pada kategori ini akan kehilangan tautan kategori.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
